// src/api/PhotosApi.js
// Handles photo uploads to Amazon S3 using presigned URLs

/**
 * Upload a file to S3 using a presigned PUT URL
 * @param {string} presignedUrl - The presigned PUT URL from the server
 * @param {File} file - The file to upload
 * @param {string} contentType - The MIME type of the file
 * @returns {Promise<{ok: boolean, error?: string}>}
 */
export const uploadFileToS3 = async (presignedUrl, file, contentType) => {
  try {
    const response = await fetch(presignedUrl, {
      method: "PUT",
      body: file,
      headers: {
        "Content-Type": contentType,
      },
    });

    if (!response.ok) {
      return {
        ok: false,
        error: `S3 upload failed with status ${response.status}`,
      };
    }

    return { ok: true };
  } catch (error) {
    return {
      ok: false,
      error: error.message || "Failed to upload file to S3",
    };
  }
};

/**
 * Delete a file from S3 using a presigned DELETE URL
 * @param {string} deleteUrl - The presigned DELETE URL from the server
 * @returns {Promise<{ok: boolean, error?: string}>}
 */
export const deleteFileFromS3 = async (deleteUrl) => {
  try {
    const response = await fetch(deleteUrl, {
      method: "DELETE",
    });

    // S3 returns 204 No Content on successful delete
    if (!response.ok && response.status !== 204) {
      return {
        ok: false,
        error: `S3 delete failed with status ${response.status}`,
      };
    }

    return { ok: true };
  } catch (error) {
    return {
      ok: false,
      error: error.message || "Failed to delete file from S3",
    };
  }
};

/**
 * Download a file from S3 for local save (using presigned GET URL)
 * @param {string} url - The presigned GET URL or direct URL
 * @param {string} filename - The filename to save as
 * @returns {Promise<{ok: boolean, error?: string}>}
 */
export const downloadFileFromS3 = async (url, filename) => {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      return {
        ok: false,
        error: `Failed to fetch photo for download (status ${response.status})`,
      };
    }

    const blob = await response.blob();
    const downloadUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(downloadUrl);

    return { ok: true };
  } catch (error) {
    return {
      ok: false,
      error: error.message || "Failed to download photo",
    };
  }
};